import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { helperAPI } from '../services/api';
import StarRating from '../components/StarRating';
import ReviewsList from '../components/ReviewsList';
import './HelperProfile.css';

const HelperProfile = () => {
  const { id } = useParams();
  const [helper, setHelper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHelper();
  }, [id]);

  const loadHelper = async () => {
    try {
      setLoading(true);
      const response = await helperAPI.getHelper(id);
      setHelper(response.data.helper);
    } catch (err) {
      setError('Helper not found or failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const serviceIcons = {
    'sweeping-mopping': '🧹',
    'cooking': '👨‍🍳',
    'laundry': '👕',
    'gardening': '🌱',
    'pet-care': '🐕',
    'elderly-care': '👴',
    'babysitting': '👶',
    'hair-dresser': '✂️',
    'other': '🔧'
  };

  const getServiceIcon = (service) => {
    return serviceIcons[service] || '🔧';
  };

  const formatService = (service) => {
    return service.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getAvailabilityDays = (availability) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    return days.map((day, index) => ({
      day: dayLabels[index],
      available: availability[day]
    }));
  };

  if (loading) {
    return (
      <div className="helper-profile-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading helper profile...</p>
        </div>
      </div>
    );
  }

  if (error || !helper) {
    return (
      <div className="helper-profile-container">
        <div className="error-message">
          <h2>❌ {error}</h2>
          <Link to="/helpers" className="back-btn">
            ← Back to Helpers
          </Link>
        </div>
      </div>
    );
  }

  const availabilityDays = getAvailabilityDays(helper.availability);

  return (
    <div className="helper-profile-container">
      {/* Header Section */}
      <div className="profile-header">
        <div className="helper-avatar-large">
          {helper.name.charAt(0).toUpperCase()}
        </div>
        <div className="helper-intro">
          <h1>{helper.name}</h1>
          <p className="helper-location">📍 {helper.location}</p>
          <div className="helper-rating">
            <StarRating 
              rating={helper.rating || 0} 
              readonly={true} 
              size="large"
              showText={true}
            />
            <span className="review-count">
              ({helper.totalReviews || 0} review{helper.totalReviews !== 1 ? 's' : ''})
            </span>
          </div>
          <div className="hourly-rate">
            <span className="rate-label">Hourly Rate:</span>
            <span className="rate-amount">₹{helper.hourlyRate}/hour</span>
          </div>
        </div>
        <div className="profile-actions">
          <Link 
            to={`/book?helperId=${helper._id}`}
            className="book-now-btn"
          >
            📅 Book Now
          </Link>
          <Link to="/helpers" className="back-link">
            ← Back to Helpers
          </Link>
        </div>
      </div>

      {/* Profile Content */}
      <div className="profile-content">
        {/* Services Section */}
        <div className="profile-section">
          <h3>🛠️ Services Offered</h3>
          <div className="services-grid">
            {helper.services.map((service) => (
              <div key={service} className="service-item">
                <span className="service-icon">{getServiceIcon(service)}</span>
                <span className="service-name">{formatService(service)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Availability Section */}
        <div className="profile-section">
          <h3>📅 Availability</h3>
          <div className="availability-grid">
            {availabilityDays.map((dayInfo) => (
              <div 
                key={dayInfo.day} 
                className={`availability-day ${dayInfo.available ? 'available' : 'unavailable'}`}
              >
                <span className="day-label">{dayInfo.day}</span>
                <span className="availability-status">
                  {dayInfo.available ? '✅' : '❌'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bio Section */}
        {helper.bio && (
          <div className="profile-section">
            <h3>📝 About</h3>
            <div className="bio-content">
              <p>{helper.bio}</p>
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="profile-section">
          <ReviewsList 
            helperId={helper._id} 
            showTitle={true}
            limit={null}
          />
        </div>
      </div>
    </div>
  );
};

export default HelperProfile; 